// Proposals card for the Deal detail page.
// Shows every proposal linked to this deal + a "New proposal" button that
// creates a draft and jumps to the builder.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, FileText, ExternalLink } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";

const STATUS_META = {
  draft:     { label: "Draft",     fg: "#64748b", bg: "rgba(100,116,139,0.14)" },
  published: { label: "Published", fg: "#5B4FE8", bg: "rgba(91,79,232,0.14)" },
  accepted:  { label: "Accepted",  fg: "#16a34a", bg: "rgba(22,163,74,0.14)" },
  rejected:  { label: "Rejected",  fg: "#dc2626", bg: "rgba(220,38,38,0.14)" },
  archived:  { label: "Archived",  fg: "#94a3b8", bg: "rgba(148,163,184,0.14)" },
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

export default function DealProposalsCard({ dealId }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/proposals?deal_id=${dealId}`);
      setRows(Array.isArray(data) ? data : []);
    } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { if (dealId) load(); /* eslint-disable-next-line */ }, [dealId]);

  const newProposal = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/proposals", { deal_id: dealId, prefill_from_deal: true });
      navigate(`/proposals/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create proposal");
    } finally { setCreating(false); }
  };

  return (
    <div className="pg-card p-6" data-testid="deal-proposals-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={14} style={{ color: "var(--pg-accent)" }} />
          <h3 className="text-sm mono uppercase text-mute tracking-wider" style={{ margin: 0 }}>Proposals</h3>
        </div>
        <button
          onClick={newProposal}
          disabled={creating}
          className="pg-btn pg-btn-primary"
          style={{ fontSize: 12, padding: "6px 12px" }}
          data-testid="deal-new-proposal-btn"
        >
          <Plus size={12} /> New proposal
        </button>
      </div>

      {loading ? (
        <div className="text-xs" style={{ color: "var(--pg-text-mute)" }}>Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-xs" style={{ color: "var(--pg-text-mute)" }}>
          No proposals yet. Send one when you're ready to price this deal.
        </div>
      ) : (
        <div className="space-y-2">
          {rows.filter(r => !r.template).map((p) => {
            const meta = STATUS_META[p.status] || STATUS_META.draft;
            return (
              <button
                key={p.id}
                onClick={() => navigate(`/proposals/${p.id}`)}
                className="w-full flex items-center justify-between p-3 text-left"
                style={{
                  background: "transparent",
                  border: "1px solid var(--pg-border)",
                  borderRadius: 8,
                  cursor: "pointer",
                  transition: "border-color 120ms",
                }}
                onMouseEnter={(e) => e.currentTarget.style.borderColor = "var(--pg-accent)"}
                onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--pg-border)"}
                data-testid={`deal-proposal-row-${p.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="mono text-[11px]" style={{ color: "var(--pg-text-mute)" }}>{p.number}</span>
                    <span style={{ fontSize: 13, fontWeight: 500 }} className="truncate">{p.name}</span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: "var(--pg-text-mute)" }}>Created {fmtDate(p.created_at)}</div>
                </div>
                <span style={{
                  display: "inline-block", padding: "2px 10px", borderRadius: 100,
                  fontSize: 11, fontWeight: 500, color: meta.fg, background: meta.bg,
                }}>{meta.label}</span>
                <ExternalLink size={12} style={{ marginLeft: 10, color: "var(--pg-text-mute)" }} />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
